import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import axios from "@utils/axios";
import { MainDetailsInterface } from "@/types/location";

export interface LocationDetailsParams {
  evseID: string;
}

const fetchLocationDetails = async (params: LocationDetailsParams, signal?: AbortSignal): Promise<MainDetailsInterface> => {
  const response = await axios.get(`/api/evse/${params.evseID}/get-location-details/`, { signal });
  return response.data as MainDetailsInterface;
};

type QueryKeyType = readonly ["location", "details", LocationDetailsParams | "disabled"];

export const locationDetailsKeys = {
  all: ["location"] as const,
  details: () => [...locationDetailsKeys.all, "details"] as const,
  detail: (params: LocationDetailsParams | "disabled") => [...locationDetailsKeys.details(), params] as const
};

export const useLocationDetails = (
  params: LocationDetailsParams | null,
  options?: Omit<UseQueryOptions<MainDetailsInterface, Error, MainDetailsInterface, QueryKeyType>, "queryKey" | "queryFn">
) => {
  const isEnabled = params !== null && !!params.evseID;

  return useQuery({
    queryKey: params ? locationDetailsKeys.detail(params) : locationDetailsKeys.detail("disabled"),
    queryFn: ({ signal }) => {
      if (!params) {
        throw new Error("Location details params are required");
      }
      return fetchLocationDetails(params, signal);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: isEnabled,
    ...options
  });
};
